import { AxiosConfigExplore } from "Explore/Config/AxiosConfig";



const normalizeEntityType = (entityType = "institution") => {
  const type = String(entityType).toLowerCase();
  if (type === "author" || type === "authors" || type === "researchers") {
    return "researcher";
  }
  if (type === "institutions") {
    return "institution";
  }
  if (type === "units") {
    return "unit";
  }
  return type || "institution";
};

/**
 * Lấy danh sách records cho Table View.
 * GET /benchmarking/{entityType}/analysis (institution | researcher | unit)
 */
export const getAnalysisRecords = async ({
  entityType = "institution",
  entityIds,         // string[] | undefined — optional, filter cứng theo entity
  yearRange,         // [number, number] | string — e.g. "all", "last_5_years", "last_10_years"
  subjectArea,       // string[] — tags từ SubjectAreaFilter (free-text, đã suggest)
  keywordsOperator,  // "or" | "and" — chỉ có khi subjectArea.length >= 2
  state,             // string[] — mảng state codes, e.g. ["CA", "NY"]
  page = 0,
  pageSize = 25,
  sortBy = "total_pubs", // mặc định luôn có sort_by là total_pubs
  sortOrder = "desc",
}) => {
  const axios = AxiosConfigExplore();
  const normalizedType = normalizeEntityType(entityType);

  const params = {
    ...(entityIds?.length && { entity_ids: entityIds }),
    year_range: yearRange || "all",
    ...(subjectArea?.length && { subject_area: subjectArea }),
    ...(subjectArea?.length >= 2 && { keywords_operator: keywordsOperator || "or" }),
    ...(state?.length && { state }),
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
  };

  const res = await axios.get(`/benchmarking/${normalizedType}/analysis`, { params });
  return res.data; // { total, page, page_size, records: AnalysisRecord[] }
};

/**
 * Lấy time-series data cho Visual View (Trend chart).
 * GET /benchmarking/{entityType}/analysis/trend (institution | researcher | unit)
 */
export const getAnalysisTrend = async ({
  entityType = "institution",
  entityIds,    // string[] — bắt buộc
  metric = "total_pubs",
  yearRange,
  subjectArea,
  keywordsOperator,
  state,
}) => {
  const axios = AxiosConfigExplore();
  const normalizedType = normalizeEntityType(entityType);

  const params = {
    ...(entityIds?.length ? { entity_ids: entityIds } : {}),
    metric,
    year_range: yearRange || "all",
    ...(subjectArea?.length && { subject_area: subjectArea }),
    ...(subjectArea?.length >= 2 && { keywords_operator: keywordsOperator || "or" }),
    ...(state?.length && { state }),
  };

  const res = await axios.get(`/benchmarking/${normalizedType}/analysis/trend`, { params });
  return res.data; // { years: number[], series: { entity_id, entity_name, values }[] }
};

/**
 * Get peer suggestions for a given entity.
 * GET /benchmarking/institution/peers
 */
export const getPeerSuggestions = async ({
  institutionId,
  yearRange = "all",
  subjectArea = [],
  keywordsOperator,
  state,
  page = 0,
  pageSize = 20,
  sortBy = "peer_score",
  sortOrder = "desc",
  minPeerScore = 0.1,
}) => {
  const axios = AxiosConfigExplore();

  const params = {
    institution_id: institutionId,
    year_range: yearRange || "all",
    ...(subjectArea?.length > 0 && { subject_area: subjectArea }),
    ...(subjectArea?.length >= 2 && { keywords_operator: keywordsOperator || "or" }),
    ...(state?.length && { state }),
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    min_peer_score: minPeerScore,
  };

  const res = await axios.get(`/benchmarking/institution/peers`, { params });
  return res.data; 
};

/**
 * Lấy báo cáo chi tiết cho một entity (Institution Report Detail).
 * POST /benchmarking/{entityType}/report_detail
 *
 * @param {Object} params
 * @param {string} [params.entityType="institution"]
 * @param {string} [params.entityId]
 * @param {string} [params.entity_id]
 * @param {string} [params.yearRange="all"]
 * @param {string} [params.year_range="all"]
 * @param {string[]} [params.subjectArea=[]]
 * @param {string[]} [params.subject_area=[]]
 * @returns {Promise<import("Explore/Config/reportDetailConfig").ReportDetailResponse>}
 */
export const getReportDetail = async ({
  entityType = "institution",
  entityId,
  entity_id,
  yearRange,
  year_range,
  subjectArea,
  subject_area,
}) => {
  const axios = AxiosConfigExplore();

  const id = entityId || entity_id;
  const range = yearRange || year_range || "all";
  const rawSubjectArea = subjectArea || subject_area || [];
  const subjectList = Array.isArray(rawSubjectArea)
    ? rawSubjectArea
    : (rawSubjectArea ? [rawSubjectArea] : []);

  const payload = {
    entity_id: id,
    year_range: range,
    // Backend accepts array of at most 1 keyword; send [] if no filter
    subject_area: subjectList.slice(0, 1),
  };

  const res = await axios.post(`/benchmarking/${entityType}/report_detail`, payload);
  return res.data;
};

export const fetchReportDetail = getReportDetail;

